import { ref } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { externalListsClient } from '~/services';
import { arraysEqual } from '~/utils/array';
import type { ClientContext } from '~/services/contextClient';
import type {
    OrgUnitExternalListsContactCountParams,
    AudienceType,
} from '~/services/externalLists/externalListsClient.types';

export const useExternalListsContactCount = createGlobalState(() => {
    // [externalListId]: contactsCount
    // undefined = loading
    // null = not found
    const contactsCount = ref<Record<number, number>>({});

    const fetching = ref(false);
    const lastGroupIds = ref<string[] | undefined>(undefined);
    const lastAudienceType = ref<AudienceType | null | undefined>(undefined);

    const resetContactsCount = () => {
        contactsCount.value = {};
        fetching.value = false;
    };

    const fetchExternalListsContactCount = async (
        context: ClientContext,
        params: OrgUnitExternalListsContactCountParams
    ) => {
        // Check if group_ids or audience_type changed - if so, reset cache
        const groupIdsChanged = !arraysEqual(params.group_ids, lastGroupIds.value);
        const audienceTypeChanged = params.audience_type !== lastAudienceType.value;

        if (groupIdsChanged || audienceTypeChanged) {
            resetContactsCount();
            lastGroupIds.value = params.group_ids;
            lastAudienceType.value = params.audience_type;
        }

        // remove existing ids to avoid computing them again
        const externalListIds = params.external_contact_list_ids.filter(
            (externalListId) => contactsCount.value[externalListId] == null
        );

        if (externalListIds.length === 0) {
            return;
        }

        if (fetching.value) {
            // when its currently making a request call wait a bit before retrieving again
            setTimeout(() => {
                fetchExternalListsContactCount(context, { ...params, external_contact_list_ids: externalListIds });
            }, 500);
            return;
        }

        try {
            fetching.value = true;

            const response = await externalListsClient.getExternalListsContactCount(context, {
                ...params,
                external_contact_list_ids: externalListIds,
            });

            externalListIds.forEach((externalListId) => {
                contactsCount.value[externalListId] = response.data.counts[externalListId] ?? 0;
            });
        } finally {
            fetching.value = false;
        }
    };

    return {
        fetchExternalListsContactCount,
        contactsCount,
    };
});
